import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Fuse from 'fuse.js';
import { Search, X, FileText, Package, Wrench, Users, Image, ArrowRight } from 'lucide-react';

interface SearchEntry {
  title: string;
  description: string;
  keywords: string;
  path: string;
  category: string;
  state?: Record<string, unknown>;
}

const searchIndex: SearchEntry[] = [
  // Pages
  { title: 'Home', description: 'Healthcare equipment installation and support services nationwide.', keywords: 'home homepage', path: '/', category: 'Page' },
  { title: 'Products', description: 'Equipment and installation services for healthcare facilities.', keywords: 'products equipment', path: '/products', category: 'Page' },
  { title: 'Services', description: 'End-to-end healthcare equipment services from assessment to maintenance.', keywords: 'services how we work', path: '/services', category: 'Page' },
  { title: 'Our Work', description: 'Project gallery, reviews, and customer facilities.', keywords: 'work gallery photos projects', path: '/work', category: 'Page' },
  { title: 'About Us', description: 'Our mission, values, team, and commitment to healthcare facilities.', keywords: 'about team staff mission values', path: '/about', category: 'Page' },
  { title: 'Contact', description: 'Get in touch for a quote or to discuss your facility needs.', keywords: 'contact quote phone email', path: '/contact', category: 'Page' },
  { title: 'Leave a Review', description: 'Share your experience working with JVP Health Tech Solutions.', keywords: 'review feedback testimonial', path: '/reviews', category: 'Page' },
  { title: 'Reviews', description: 'Read feedback from healthcare professionals we\'ve worked with.', keywords: 'reviews testimonials feedback ratings', path: '/work', category: 'Page', state: { tab: 'reviews' } },
  { title: 'Customers', description: 'Hospitals and health systems we\'ve served nationwide.', keywords: 'customers clients hospitals facilities', path: '/work', category: 'Page', state: { tab: 'customers' } },
  { title: 'Privacy Policy', description: 'How we collect, use, and protect your information.', keywords: 'privacy policy data', path: '/privacy-policy', category: 'Page' },
  { title: 'Terms & Conditions', description: 'Rules and guidelines governing use of our website and services.', keywords: 'terms conditions legal', path: '/terms', category: 'Page' },

  // Products
  { title: 'Hospital Workstations', description: 'Fixed and adjustable workstations for clinical environments with height-adjustable surfaces, cable management, and antimicrobial materials.', keywords: 'workstation desk height adjustable ADA JCO', path: '/products', category: 'Product' },
  { title: 'Patient Monitors', description: 'Mounting and integration solutions for patient monitors across all major brands including nurse stations, roller stands, wall mounts, and anesthesia carts.', keywords: 'patient monitor arm mount ceiling wall roller stand anesthesia', path: '/products', category: 'Product' },
  { title: 'Mobile Workstations', description: 'Powered and non-powered mobile carts for bedside computing and clinical tools.', keywords: 'mobile cart battery powered lockable ergonomic', path: '/products', category: 'Product' },
  { title: 'Virtual Nursing Systems', description: 'Integrated technology setups for virtual and hybrid nursing models with camera, display, and two-way communication.', keywords: 'virtual nursing telehealth remote monitoring camera', path: '/products', category: 'Product' },
  { title: 'Specialty Installations', description: 'Custom equipment configurations for ORs, ICUs, procedure rooms, and imaging suites.', keywords: 'specialty OR ICU boom pendant custom', path: '/products', category: 'Product' },
  { title: 'Pharmacy', description: 'Workstation and storage solutions for pharmacy environments supporting medication management and dispensing workflows.', keywords: 'pharmacy dispensing medication barcode', path: '/products', category: 'Product' },

  // Services
  { title: 'Site Assessment & Planning', description: 'Comprehensive evaluation of your space, workflow, and infrastructure for equipment installation.', keywords: 'assessment planning consulting evaluation site visit', path: '/services/consulting', category: 'Service' },
  { title: 'Equipment Trials & Demos', description: 'On-site trials to validate ergonomics, reach, and workflow compatibility before final selection.', keywords: 'trial demo evaluation test hands-on', path: '/services/demos', category: 'Service' },
  { title: 'Equipment Installation', description: 'Professional installation for articulating arms, patient monitors, mobile carts, workstations, and track-mounted systems.', keywords: 'installation install mounting setup room configuration', path: '/services/installation', category: 'Service' },
  { title: 'Training & Education', description: 'Hands-on training for clinical and support staff on proper use, safety, and best practices.', keywords: 'training education staff clinical', path: '/services/training', category: 'Service' },
  { title: 'Support & Maintenance', description: 'Preventive maintenance, fleet servicing, parts sourcing, and equipment relocation.', keywords: 'maintenance support repair parts fleet preventive', path: '/services/maintenance', category: 'Service' },

  // Staff
  { title: 'Joe Penwell', description: 'Project Manager — leads equipment installation projects.', keywords: 'Joe Penwell project manager staff team', path: '/about', category: 'Team' },
  { title: 'Valerie Penwell', description: 'Director of Operations — oversees operations.', keywords: 'Valerie Penwell director of operations staff team', path: '/about', category: 'Team' },

  // Gallery Categories
  { title: 'Workstations Gallery', description: 'Photos of hospital workstation installations.', keywords: 'workstation desk gallery photos', path: '/work', category: 'Gallery', state: { category: 'work_station' } },
  { title: 'Patient Monitors Gallery', description: 'Photos of patient monitor mounting installations.', keywords: 'patient monitor arm gallery photos', path: '/work', category: 'Gallery', state: { category: 'patient_monitor' } },
  { title: 'Mobile Workstations Gallery', description: 'Photos of mobile workstation and cart deployments.', keywords: 'mobile workstation cart gallery photos', path: '/work', category: 'Gallery', state: { category: 'mobile_workstation' } },
  { title: 'Ceiling Mounts Gallery', description: 'Photos of ceiling-mounted equipment installations.', keywords: 'ceiling mount arm gallery photos', path: '/work', category: 'Gallery', state: { category: 'ceiling_mount' } },
  { title: 'Boom Mounts Gallery', description: 'Photos of boom mount installations.', keywords: 'boom mount pendant gallery photos', path: '/work', category: 'Gallery', state: { category: 'boom_mounts' } },
  { title: 'Nurse Stations Gallery', description: 'Photos of desk-mounted nurse station setups.', keywords: 'nurse station desk mount gallery photos', path: '/work', category: 'Gallery', state: { category: 'desk_mount_nurse_station' } },
  { title: 'Anesthesia Carts Gallery', description: 'Photos of anesthesia cart installations.', keywords: 'anesthesia cart gallery photos', path: '/work', category: 'Gallery', state: { category: 'anesthesia_carts' } },
  { title: 'Pharmacy Gallery', description: 'Photos of pharmacy workstation and storage installations.', keywords: 'pharmacy gallery photos', path: '/work', category: 'Gallery', state: { category: 'pharmacy' } },
  { title: 'Specialty Gallery', description: 'Photos of specialty clinical installations.', keywords: 'specialty OR ICU gallery photos', path: '/work', category: 'Gallery', state: { category: 'specialty' } },
  { title: 'Installations Gallery', description: 'General installation project photos.', keywords: 'installation install gallery photos', path: '/work', category: 'Gallery', state: { category: 'install_pics' } },
  { title: 'Before & After Gallery', description: 'Before and after photos of equipment installations.', keywords: 'before after comparison gallery photos', path: '/work', category: 'Gallery', state: { category: 'before_after' } },
  { title: 'Clinic Rooms Gallery', description: 'Photos of clinic room configurations.', keywords: 'clinic room gallery photos', path: '/work', category: 'Gallery', state: { category: 'clinic_rooms' } },
  { title: 'Lab Gallery', description: 'Photos of lab equipment installations.', keywords: 'lab laboratory gallery photos', path: '/work', category: 'Gallery', state: { category: 'lab_pics' } },
  { title: 'Med Prep Gallery', description: 'Photos of medication preparation area setups.', keywords: 'med prep medication gallery photos', path: '/work', category: 'Gallery', state: { category: 'med_prep' } },
  { title: 'Repairs Gallery', description: 'Photos of equipment repair and servicing work.', keywords: 'repair service fix gallery photos', path: '/work', category: 'Gallery', state: { category: 'repairs' } },
  { title: 'Sterile Processing Gallery', description: 'Photos of sterile processing area installations.', keywords: 'sterile processing gallery photos', path: '/work', category: 'Gallery', state: { category: 'sterile_processing' } },
  { title: 'Strong Arm Gallery', description: 'Photos of Strong Arm equipment installations.', keywords: 'strong arm gallery photos', path: '/work', category: 'Gallery', state: { category: 'strong_arm_install' } },
  { title: 'Dual Monitors Gallery', description: 'Photos of dual monitor mounting installations.', keywords: 'dual monitor double screen gallery photos', path: '/work', category: 'Gallery', state: { category: 'dual_monitor_pics' } },
  { title: 'PC Mounts Gallery', description: 'Photos of PC and computer mounting installations.', keywords: 'PC mount computer gallery photos', path: '/work', category: 'Gallery', state: { category: 'pc_mount_pics' } },
  { title: 'Dri-Scope Gallery', description: 'Photos of Dri-Scope installations.', keywords: 'dri-scope dri scope gallery photos', path: '/work', category: 'Gallery', state: { category: 'dri_scope_installs' } },
  { title: 'Walkthroughs Gallery', description: 'Photos from facility walkthrough assessments.', keywords: 'walkthrough assessment evaluation gallery photos', path: '/work', category: 'Gallery', state: { category: 'walkthrough_pics' } },
  { title: 'Evaluations Gallery', description: 'Photos from equipment evaluation sessions.', keywords: 'evaluation assessment gallery photos', path: '/work', category: 'Gallery', state: { category: 'eval_pics' } },
  { title: 'Ergotron Gallery', description: 'Photos of Ergotron equipment installations.', keywords: 'ergotron gallery photos', path: '/work', category: 'Gallery', state: { category: 'ergotron' } },
  { title: 'Wall Mounts Gallery', description: 'Photos of wall-mounted equipment installations.', keywords: 'wall mount gallery photos', path: '/work', category: 'Gallery', state: { category: 'wall_mounts' } },
];

const fuseOptions = {
  keys: [
    { name: 'title', weight: 0.4 },
    { name: 'description', weight: 0.3 },
    { name: 'keywords', weight: 0.2 },
    { name: 'category', weight: 0.1 },
  ],
  threshold: 0.35,
  includeScore: true,
  minMatchCharLength: 2,
};

const ICON_STYLE = process.env.REACT_APP_SEARCH_ICONS === 'true';
const SHOW_DESC = process.env.REACT_APP_SEARCH_DESCRIPTIONS !== 'false';
const GROUPED = process.env.REACT_APP_SEARCH_GROUPED === 'true';
const COMPACT = process.env.REACT_APP_SEARCH_COMPACT === 'true';
const HIGHLIGHT = process.env.REACT_APP_SEARCH_HIGHLIGHT === 'true';
const DARK = process.env.REACT_APP_SEARCH_DARK === 'true';

const categoryMeta: Record<string, { icon: React.FC<{ size?: number; strokeWidth?: number }>; color: string }> = {
  Page: { icon: FileText, color: '#6b7280' },
  Product: { icon: Package, color: '#2563eb' },
  Service: { icon: Wrench, color: '#059669' },
  Team: { icon: Users, color: '#7c3aed' },
  Gallery: { icon: Image, color: '#d97706' },
};

const highlightMatch = (text: string, q: string): React.ReactNode => {
  if (!HIGHLIGHT || !q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return <>{text.slice(0, idx)}<mark className="search-highlight">{text.slice(idx, idx + q.length)}</mark>{text.slice(idx + q.length)}</>;
};

const categoryOrder = ['Page', 'Product', 'Service', 'Team', 'Gallery'];

const SearchBar: React.FC = () => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const fuse = useMemo(() => new Fuse(searchIndex, fuseOptions), []);
  const results = useMemo(() => query.length >= 2 ? fuse.search(query).slice(0, 8) : [], [fuse, query]);

  const flatItems = useMemo(() => results.map(r => r.item), [results]);

  const grouped = useMemo(() => {
    if (!GROUPED) return null;
    const map = new Map<string, SearchEntry[]>();
    for (const r of results) {
      const list = map.get(r.item.category) || [];
      list.push(r.item);
      map.set(r.item.category, list);
    }
    return categoryOrder.filter(c => map.has(c)).map(c => ({ category: c, items: map.get(c)! }));
  }, [results]);

  const close = () => { setOpen(false); setQuery(''); setSelectedIndex(-1); };

  const goTo = useCallback((entry: SearchEntry) => {
    close();
    navigate(entry.path, entry.state ? { state: entry.state } : undefined);
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [navigate]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { close(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(i => Math.max(i - 1, -1)); }
    if (e.key === 'Enter' && selectedIndex >= 0 && results[selectedIndex]) {
      e.preventDefault();
      goTo(results[selectedIndex].item);
    }
  };

  const renderItem = useCallback((item: SearchEntry, i: number) => (
    <li
      key={`${item.path}-${item.title}`}
      role="option"
      aria-selected={i === selectedIndex}
      className={`search-result-item ${ICON_STYLE ? 'search-result-icons' : ''} ${i === selectedIndex ? 'search-result-active' : ''}`}
      onClick={() => goTo(item)}
      onMouseEnter={() => setSelectedIndex(i)}
    >
      {ICON_STYLE ? (
        <>
          {(() => { const cat = categoryMeta[item.category]; const Icon = cat?.icon || FileText; return (
            <div className="search-result-icon" style={{ color: cat?.color || '#6b7280' }}>
              <Icon size={18} strokeWidth={1.8} />
            </div>
          ); })()}
          <div className="search-result-content">
            <span className="search-result-title">{highlightMatch(item.title, query)}</span>
            {SHOW_DESC && <span className="search-result-desc">{highlightMatch(item.description, query)}</span>}
          </div>
          <ArrowRight size={14} className="search-result-arrow" />
        </>
      ) : (
        <>
          <div className="search-result-top">
            <span className="search-result-title">{highlightMatch(item.title, query)}</span>
            <span className="search-result-badge" style={{ background: `${categoryMeta[item.category]?.color || '#6b7280'}15`, color: categoryMeta[item.category]?.color || '#6b7280' }}>
              {item.category}
            </span>
          </div>
          {SHOW_DESC && <span className="search-result-desc">{highlightMatch(item.description, query)}</span>}
        </>
      )}
    </li>
  ), [selectedIndex, query, goTo]);

  return (
    <div ref={containerRef} className="search-container">
      <div className="search-input-wrap">
        <Search size={16} color="var(--color-text-muted-strong)" strokeWidth={2} />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search..."
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); setSelectedIndex(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          className="search-input"
          aria-label="Search site"
          aria-expanded={open && results.length > 0}
          role="combobox"
          aria-controls="search-results"
          aria-autocomplete="list"
        />
        {query && (
          <button onClick={close} className="search-clear" aria-label="Clear search">
            <X size={14} color="var(--color-text-muted-strong)" strokeWidth={2} />
          </button>
        )}
      </div>

      {open && query.length >= 2 && (
        <ul id="search-results" className={`search-results${COMPACT ? ' search-compact' : ''}${DARK ? ' search-dark' : ''}`} role="listbox">
          {results.length > 0 ? (
            GROUPED && grouped ? (
              grouped.map(group => {
                const cat = categoryMeta[group.category];
                return (
                  <React.Fragment key={group.category}>
                    <li className="search-group-header" style={{ color: cat?.color }}>{group.category}s</li>
                    {group.items.map(item => {
                      const i = flatItems.indexOf(item);
                      return renderItem(item, i);
                    })}
                  </React.Fragment>
                );
              })
            ) : (
              results.map((r, i) => renderItem(r.item, i))
            )
          ) : (
            <li className="search-no-results">No results found</li>
          )}
        </ul>
      )}
    </div>
  );
};

export default SearchBar;
